export type DashboardWidgetId =
  | "appointments_today"
  | "day_estimate"
  | "pendings"
  | "insights"
  | "kpi_clients"
  | "kpi_professionals"
  | "kpi_appointments_today"
  | "kpi_orders_open"
  | "kpi_packages_expiring"
  | "quick_actions"
  | "recent_improvements";

export type DashboardWidgetLayoutItem = {
  id: DashboardWidgetId;
  visible: boolean;
  order: number;
};

export const DASHBOARD_CATALOG_IDS: DashboardWidgetId[] = [
  "appointments_today",
  "day_estimate",
  "pendings",
  "insights",
  "kpi_clients",
  "kpi_professionals",
  "kpi_appointments_today",
  "kpi_orders_open",
  "kpi_packages_expiring",
  "quick_actions",
  "recent_improvements",
];

export const DASHBOARD_WIDGET_ROLES: Record<DashboardWidgetId, string[]> = {
  appointments_today: ["owner", "manager", "receptionist", "sdr"],
  day_estimate: ["owner", "manager", "receptionist"],
  pendings: ["owner", "manager", "receptionist"],
  insights: ["owner", "manager"],
  kpi_clients: ["owner", "manager", "receptionist", "sdr"],
  kpi_professionals: ["owner", "manager", "receptionist"],
  kpi_appointments_today: ["owner", "manager", "receptionist", "sdr"],
  kpi_orders_open: ["owner", "manager", "receptionist"],
  kpi_packages_expiring: ["owner", "manager", "receptionist"],
  quick_actions: ["owner", "manager", "receptionist", "sdr"],
  recent_improvements: ["owner", "manager"],
};

/** Operational default — agenda + day money + pendings + indicators (GAP-F2-147). */
export const DASHBOARD_DEFAULT_ORDER: DashboardWidgetId[] = [
  "appointments_today",
  "day_estimate",
  "pendings",
  "insights",
  "kpi_appointments_today",
  "kpi_orders_open",
  "kpi_clients",
  "kpi_professionals",
  "kpi_packages_expiring",
  "quick_actions",
  "recent_improvements",
];

/** Visible out of the box for new tenants / “Restaurar padrão”. */
export const DASHBOARD_DEFAULT_VISIBLE = new Set<DashboardWidgetId>([
  "appointments_today",
  "day_estimate",
  "pendings",
  "insights",
]);

export const DASHBOARD_KPI_IDS = new Set<DashboardWidgetId>([
  "kpi_clients",
  "kpi_professionals",
  "kpi_appointments_today",
  "kpi_orders_open",
  "kpi_packages_expiring",
]);

export const DASHBOARD_CUSTOMIZE_ROLES = ["owner", "manager", "receptionist"] as const;

export const isKpiWidget = (id: DashboardWidgetId): boolean =>
  DASHBOARD_KPI_IDS.has(id);

export const canCustomizeDashboard = (roles: string[]): boolean =>
  roles.some((role) =>
    (DASHBOARD_CUSTOMIZE_ROLES as readonly string[]).includes(role)
  );

export const isEligibleWidget = (
  id: DashboardWidgetId,
  roles: string[]
): boolean => {
  const allowed = DASHBOARD_WIDGET_ROLES[id] ?? [];
  return allowed.some((role) => roles.includes(role));
};

export const eligibleWidgetIds = (roles: string[]): DashboardWidgetId[] =>
  DASHBOARD_CATALOG_IDS.filter((id) => isEligibleWidget(id, roles));

export const buildDefaultWidgets = (
  roles: string[]
): DashboardWidgetLayoutItem[] => {
  const eligible = eligibleWidgetIds(roles);
  const ordered = [
    ...DASHBOARD_DEFAULT_ORDER.filter((id) => eligible.includes(id)),
    ...eligible.filter((id) => !DASHBOARD_DEFAULT_ORDER.includes(id)),
  ];

  return ordered.map((id, order) => ({
    id,
    visible: DASHBOARD_DEFAULT_VISIBLE.has(id),
    order,
  }));
};

export type DashboardLayoutPreset = "balcao" | "dono";

/** Reception desk: day queue first; hide owner-heavy blocks. */
const BALCAO_VISIBLE = new Set<DashboardWidgetId>([
  "appointments_today",
  "pendings",
  "day_estimate",
  "kpi_orders_open",
  "kpi_appointments_today",
]);

const BALCAO_ORDER: DashboardWidgetId[] = [
  "appointments_today",
  "pendings",
  "day_estimate",
  "kpi_appointments_today",
  "kpi_orders_open",
  "kpi_clients",
  "quick_actions",
  "insights",
  "kpi_professionals",
  "kpi_packages_expiring",
  "recent_improvements",
];

export const applyLayoutPreset = (
  preset: DashboardLayoutPreset,
  roles: string[]
): DashboardWidgetLayoutItem[] => {
  const eligible = eligibleWidgetIds(roles);
  const eligibleSet = new Set(eligible);

  if (preset === "dono") {
    return buildDefaultWidgets(roles);
  }

  const ordered = [
    ...BALCAO_ORDER.filter((id) => eligibleSet.has(id)),
    ...eligible.filter((id) => !BALCAO_ORDER.includes(id)),
  ];

  const result = ordered.map((id, order) => ({
    id,
    visible: BALCAO_VISIBLE.has(id),
    order,
  }));

  if (result.length > 0 && !result.some((item) => item.visible)) {
    result[0] = { ...result[0], visible: true };
  }

  return result;
};

export const hasVisibleKpiWidgets = (
  widgets: DashboardWidgetLayoutItem[]
): boolean => widgets.some((item) => item.visible && isKpiWidget(item.id));

export const mergeLayoutPreference = (
  saved: DashboardWidgetLayoutItem[] | null | undefined,
  roles: string[]
): DashboardWidgetLayoutItem[] => {
  const eligible = eligibleWidgetIds(roles);
  const eligibleSet = new Set(eligible);
  const byId = new Map<DashboardWidgetId, DashboardWidgetLayoutItem>();

  (saved ?? []).forEach((row, index) => {
    if (!row?.id || !eligibleSet.has(row.id) || byId.has(row.id)) {
      return;
    }
    byId.set(row.id, {
      id: row.id,
      visible: row.visible !== false,
      order: typeof row.order === "number" ? row.order : index,
    });
  });

  const orderedIds = Array.from(byId.values())
    .sort((a, b) => a.order - b.order)
    .map((item) => item.id);

  // No saved layout → operational default (agenda-first).
  if (orderedIds.length === 0) {
    return buildDefaultWidgets(roles);
  }

  DASHBOARD_DEFAULT_ORDER.forEach((id) => {
    if (eligibleSet.has(id) && !orderedIds.includes(id)) {
      orderedIds.push(id);
      byId.set(id, {
        id,
        visible: DASHBOARD_DEFAULT_VISIBLE.has(id),
        order: orderedIds.length - 1,
      });
    }
  });

  const result = orderedIds.map((id, order) => ({
    id,
    visible: byId.get(id)?.visible !== false,
    order,
  }));

  if (result.length > 0 && !result.some((item) => item.visible)) {
    result[0] = { ...result[0], visible: true };
  }

  return result;
};

export type DashboardRenderGroup =
  | { type: "kpi_row"; ids: DashboardWidgetId[] }
  | { type: "block"; id: DashboardWidgetId }
  | { type: "day_row"; ids: Array<"day_estimate" | "pendings"> };

/** Groups consecutive visible KPIs into one row; day_estimate+pendings share a row. */
export const groupVisibleWidgets = (
  widgets: DashboardWidgetLayoutItem[]
): DashboardRenderGroup[] => {
  const visible = widgets
    .filter((item) => item.visible)
    .sort((a, b) => a.order - b.order);

  const groups: DashboardRenderGroup[] = [];
  let kpiBuffer: DashboardWidgetId[] = [];
  let dayBuffer: Array<"day_estimate" | "pendings"> = [];

  const flushKpis = () => {
    if (kpiBuffer.length === 0) {
      return;
    }
    groups.push({ type: "kpi_row", ids: [...kpiBuffer] });
    kpiBuffer = [];
  };

  const flushDay = () => {
    if (dayBuffer.length === 0) {
      return;
    }
    groups.push({ type: "day_row", ids: [...dayBuffer] });
    dayBuffer = [];
  };

  visible.forEach((item) => {
    if (isKpiWidget(item.id)) {
      flushDay();
      kpiBuffer.push(item.id);
      return;
    }
    if (item.id === "day_estimate" || item.id === "pendings") {
      flushKpis();
      dayBuffer.push(item.id);
      return;
    }
    flushKpis();
    flushDay();
    groups.push({ type: "block", id: item.id });
  });
  flushKpis();
  flushDay();

  return groups;
};
